/**
 * EthosBoard Industries page content. Source: Industries mockup.
 * Edit text here, not in the JSX.
 * `photo`: import an image and set it per industry; null shows a placeholder.
 */

import oilGas from '../../assets/images/eb/industries/oil_gas_industry.png';
import pharma from '../../assets/images/eb/industries/health_pharma.png';
import bfsi from '../../assets/images/eb/industries/bfsi.png';
import manufacturing from '../../assets/images/eb/industries/manufacturing.png';
import aviation from '../../assets/images/eb/industries/aviation.png';
import ecommerce from '../../assets/images/eb/industries/ecommerce.png';
import logistics from '../../assets/images/eb/industries/logistics.png';
import itsoftware from '../../assets/images/eb/industries/itsoftware.png';

export const IND_HERO = {
  eyebrow: 'Industries · Compliance training software',
  title: 'Compliance training and workforce readiness software built for your industry',
  subtitle:
    'Every industry has its own rules, risks and rhythms. EthosBoard turns them into role-based skills, practice and proof, so you can show your people are ready before the audit, the shift or the launch.',
  primaryCta: 'Book a demo',
  secondaryCta: 'Explore the eight industries',
  wavesLabel: 'RELEASED IN THREE WAVES',
};

export const WAVES = [
  { n: '1', label: 'Wave 1', title: 'Where the cost of getting it wrong is highest', list: 'Oil, Gas and Energy · Healthcare and Pharma · BFSI' },
  { n: '2', label: 'Wave 2', title: 'Precision industries that run on proof', list: 'Manufacturing · Aviation and Aerospace' },
  { n: '3', label: 'Wave 3', title: 'Fast-moving sectors where speed and trust compete', list: 'E-Commerce and Retail · Logistics · IT and Software' },
];

export const IND_WHAT = {
  title: 'What is industry-specific workforce readiness?',
  lead: 'The ability to prove, role by role, that employees can perform safely and compliantly under the rules of their sector.',
  text: 'A refinery operator, a pharma quality analyst and a bank relationship manager answer to different regulators, face different failure points and must produce different evidence. EthosBoard industry packs give each of them a starting point built for their world, ready to adapt to yours.',
  changesLabel: 'Three things change from one industry to the next',
  changes: [
    { icon: 'book', k: 'The rulebook', d: 'Regulators expect documented, role-specific competence, not attendance sheets.' },
    { icon: 'shield', k: 'The cost of a mistake', d: 'A missed refresher can cost a shipment, a licence or a life.' },
    { icon: 'users', k: 'The shape of the workforce', d: 'Offshore crews, shift operators, store associates or remote engineers, rarely at a desk.' },
  ],
  closing: 'Course completion rates answer none of those pressures. Readiness does.',
};

export const IND_PACK = {
  eyebrow: 'Industry packs',
  title: 'What every pack adds from day one',
  textBefore: 'Industry packs are add-ons to the Start, Grow and Scale editions on our ',
  linkLabel: 'pricing page',
  linkTo: '/pricing',
  textAfter: '. Our content services team can tailor any pack to your procedures, terminology and brand.',
  items: [
    { k: 'Role and skills map', d: 'Job roles linked to the competencies your industry expects.' },
    { k: 'Regulation-aware content', d: 'Courses and assessments structured around the standards auditors cite.' },
    { k: 'Scenario practice', d: 'Rehearse the hard moments before they happen.' },
    { k: 'Readiness and audit reports', d: 'Role, team and site readiness with evidence for an inspector.' },
    { k: 'Language and localization', d: 'Content in the languages your workforce speaks.' },
  ],
};

export const IND_LIST = {
  allLabel: 'All eight',
  allEyebrow: 'Eight industries, three waves',
  allTitle: 'Industry packs for regulated and frontline work',
  items: [
    {
      wave: 1, id: 'oil-gas-energy', photo: oilGas,
      name: 'Oil, Gas and Energy',
      intro: 'Competence and compliance are not the same thing. Crews rotate between sites, contractors arrive in large numbers and the work happens far from any classroom. Oil and gas training software has to reach people on site and record competence, not attendance.',
      points: [
        'Map operator, technician, supervisor and contractor roles to safety-critical skills such as permit to work, gas testing, process safety and emergency response.',
        'Deliver mobile lessons and scenario drills to crews on rotation, structured around frameworks such as OPITO, NEBOSH, ISO 14001 and IOGP guidance.',
        'See site and asset readiness before a shutdown, turnaround or start-up.',
      ],
      outcome: 'fewer unknowns on site and a clear record of who is competent to do what.',
    },
    {
      wave: 1, id: 'healthcare-pharma', photo: pharma,
      name: 'Healthcare and Pharma',
      intro: 'Training is a regulatory mandate. Under 21 CFR 211.25, people who make drug products need the education, training and experience for their assigned tasks, and EU GMP guidance expects documented initial and ongoing training.',
      points: [
        'Use EthosBoard as your GxP training software: assign curricula by role, site and SOP version.',
        'Keep time-stamped completion records and assessment results ready for inspection, with audit trails designed to support 21 CFR Part 11 and EU Annex 11 expectations.',
        'Give hospital and clinic teams competency checks for infection prevention, patient safety and data privacy.',
      ],
      outcome: 'audit-ready training evidence without the spreadsheet chase.',
    },
    {
      wave: 1, id: 'bfsi', photo: bfsi,
      name: 'BFSI: Banking, Financial Services and Insurance',
      intro: 'A moving rulebook and supervisors who expect proof of comprehension, not only completion. The Central Bank of the UAE asks licensed institutions for role-based AML, CFT and CPF training, with annual needs assessments.',
      points: [
        'Build role-based banking compliance training tracks for the Board, senior management, front-line staff, relationship managers and compliance teams.',
        'Track mandatory training with escalation for overdue learners, including staff based abroad.',
        'Practise fraud, sanctions, conduct and data protection decisions through scenarios rather than slides.',
      ],
      outcome: 'a defensible training record and a workforce that spots red flags earlier.',
    },
    {
      wave: 2, id: 'manufacturing', photo: manufacturing,
      name: 'Manufacturing',
      intro: 'Manufacturers are short of skills as much as people. Plants run shifts, lines change and new equipment arrives, and ISO 9001 and ISO 45001 expect evidence that people are competent for the work they do.',
      points: [
        'Build skills matrices by line, machine and shift, and see who can run what.',
        'Teach in minutes at the point of work with microlearning and mobile practice.',
        'Trigger retraining automatically when a procedure, product or machine changes.',
        "Turn senior operators' know-how into short lessons with the AI course builder.",
      ],
      outcome: 'faster ramp-up, safer lines and fewer quality escapes.',
    },
    {
      wave: 2, id: 'aviation-aerospace', photo: aviation,
      name: 'Aviation and Aerospace',
      intro: 'Aviation runs on traceability. Under EASA Part-145, maintenance organisations must control and demonstrate the competence of the people who work on aircraft, including initial and recurrent human factors training.',
      points: [
        'Track authorisations and recurrent training due dates for certifying staff, planners, stores and quality teams.',
        'Deliver recurrent topics such as human factors, safety management, fuel tank safety and EWIS.',
        'Produce competence records on demand for authority and customer audits.',
      ],
      outcome: 'continuous airworthiness backed by continuous evidence of competence.',
    },
    {
      wave: 3, id: 'ecommerce-retail', photo: ecommerce,
      name: 'E-Commerce and Retail',
      intro: 'Seasonal hires, store associates and support agents need product knowledge, service standards and safe handling of customer data on their first shift. PCI DSS v4.0.1 requires security awareness training at hire and at least every 12 months.',
      points: [
        'Onboard seasonal and new staff with short mobile paths that get them serving customers in days.',
        'Track the annual PCI DSS awareness clock for each person from their own start date.',
        'Keep launches, promotions and returns policies current across every store, in local languages.',
      ],
      outcome: 'a consistent customer experience and less payment-data risk.',
    },
    {
      wave: 3, id: 'logistics', photo: logistics,
      name: 'Logistics',
      intro: 'High volumes across depots, shifts and borders. The IATA Dangerous Goods Regulations require recurrent training at least every 24 months, and carriers can refuse shipments when training has lapsed.',
      points: [
        'Schedule 24-month dangerous goods renewals per person, with alerts before validity ends.',
        'Train drivers, warehouse teams and dispatchers on safe handling, load securing and incident response.',
        'Standardise documentation and service routines across multilingual, multi-depot teams.',
      ],
      outcome: 'fewer refused shipments, safer docks and consistent service at every depot.',
    },
    {
      wave: 3, id: 'it-software', photo: itsoftware,
      name: 'IT and Software',
      intro: 'Software companies sell on trust. Enterprise buyers ask for ISO 27001 or SOC 2 evidence, and ISO 27001 separates awareness, which everyone needs, from competence in security-affecting roles.',
      points: [
        'Assign ISO 27001 awareness to everyone and deeper competence tracks to developers, administrators, support engineers and HR.',
        'Teach secure coding, incident response and responsible AI use through hands-on scenarios.',
        'Export evidence for audits and customer security questionnaires.',
      ],
      outcome: 'shorter audit preparation and engineers who are productive sooner.',
    },
  ],
};

export const IND_FAQS = [
  { q: 'Which industries does EthosBoard serve?', a: 'EthosBoard serves eight industries in three waves. Wave 1 covers Oil, Gas and Energy, Healthcare and Pharma, and BFSI. Wave 2 covers Manufacturing and Aviation and Aerospace. Wave 3 covers E-Commerce and Retail, Logistics, and IT and Software.' },
  { q: 'What is compliance training software?', a: 'Compliance training software assigns, delivers and records the mandatory training that regulators and auditors expect. EthosBoard goes a step further: it ties each requirement to a role, checks that people can apply it, and keeps time-stamped evidence for audits.' },
  { q: 'What is an EthosBoard industry pack?', a: 'An add-on that gives a sector a ready starting point: role and skills maps, regulation-aware content and assessments, scenario practice, readiness reports and localization. Your team can adapt every element.' },
  { q: 'How is workforce readiness different from training completion?', a: 'Training completion shows that someone finished a course. Workforce readiness shows whether a person can do the job to the standard your industry requires.' },
  { q: 'Can EthosBoard produce evidence for auditors and regulators?', a: 'Yes. EthosBoard keeps time-stamped learning records, assessment results and readiness reports that you can export for audits. We map each pack to the standards that apply to you during onboarding.' },
  { q: 'Does EthosBoard work if my industry is not listed?', a: 'Yes. EthosBoard is industry-agnostic at its core, and Infylearn Technologies can build custom content, simulations and translations for any sector.' },
];

export const IND_CTA = {
  title: "See EthosBoard through your industry's eyes",
  text: 'We will walk through the role maps, practice scenarios and readiness reports for your sector. Prefer to start small? Ask about a pilot call and test EthosBoard with one team before you commit.',
  button: 'Book a demo',
};